<template>
  <div
    class="c-checkbox"
    :class="{
      'relative flex items-start': true,
      disabled,
    }"
  >
    <div class="flex items-center h-5 gap-2">
      <input
        :aria-required="required"
        :checked="checked"
        :disabled="disabled"
        :id="id"
        :name="name"
        :value="value"
        class="form-checkbox h-4 w-4 rounded shadow-sm border-input bg-white dark:bg-gray-700 text-blue-600"
        ref="input"
        type="checkbox"
        v-bind="$attrs"
        v-model="model"
      />

      <slot name="label">
        <label
          :for="id"
          :class="{
            'mb-0': true,
            'font-medium text-sm leading-5': true,
            'text-red-800 dark:text-red-200': invalid,
          }"
        >
          {{ label }}
          <RequiredIndicator v-if="required" />
        </label>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
  import RequiredIndicator from '@/common/ui/components/RequiredIndicator.vue';
  import {computed, ref} from 'vue';

  const props = withDefaults(
    defineProps<{
      modelValue: any;
      disabled?: boolean;
      id?: string;
      checked?: boolean;
      invalid?: boolean;
      label?: string;
      name?: string;
      value?: string | boolean | number;
      required?: boolean;
    }>(),
    {
      modelValue: false,
      disabled: false,
      id: () => 'checkbox-' + Math.random().toString(36).substr(2, 9),
      checked: false,
      invalid: false,
      label: '',
      name: '',
      value: false,
      required: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean | string): void;
    (e: 'change', value: boolean | string): void;
  }>();

  // Refs
  const inputRef = ref<HTMLInputElement | null>(null);

  const model = computed({
    get() {
      return props.modelValue;
    },
    set(newValue: boolean) {
      emit('update:modelValue', newValue);
    },
  });

  const click = (): void => {
    inputRef.value?.click();
  };

  const onChange = (): void => {
    emit('change', props.modelValue);
  };

  // Expose methods
  defineExpose({
    click,
    onChange,
  });
</script>

<style>
  .c-checkbox.disabled {
    @apply opacity-50;
  }
</style>
